/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { DateRangePicker, DateRange } from "@app/vue/components/UIComponents";
import { Colleague } from "@lemmonhq/types";
import { useAuth } from "@app/vue/providers/Auth";

export type ClosingReportsPageFilter = {
  userId?: number;
  dateRange: DateRange;
};

export type ClosingReportFiltersProps = {
  filters: ClosingReportsPageFilter;
  users: Colleague[];
  onFilterChange: (filters: ClosingReportsPageFilter) => void;
  /** Called after Apply — lets PageHeader close the mobile sheet. */
  onApply?: () => void;
};

export const ClosingReportFilters = defineComponent<ClosingReportFiltersProps>({
  name: "ClosingReportFilters",
  props: ["filters", "users", "onFilterChange", "onApply"],
  setup(props) {
    const auth = useAuth();
    const isManager = auth.user.value?.role === "manager";

    // Pending state for mobile — committed only on Apply.
    const pending = ref<ClosingReportsPageFilter>({ ...props.filters });

    const setDesktop = (patch: Partial<ClosingReportsPageFilter>) =>
      props.onFilterChange({ ...props.filters, ...patch });

    const applySheet = () => {
      props.onFilterChange({ ...pending.value });
      props.onApply?.();
    };

    // ── Desktop: applies immediately ─────────────────────────────────────────
    const renderDesktop = () => (
      <>
        <DateRangePicker
          value={props.filters.dateRange}
          disabled={!isManager}
          onChange={(dateRange) => setDesktop({ dateRange })}
        />
      </>
    );

    // ── Mobile: stacked with pending state + Apply ────────────────────────────
    const renderMobileSheet = () => (
      <div class="flex flex-col gap-5 p-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
            Date Range
          </label>
          <DateRangePicker
            value={pending.value.dateRange}
            disabled={!isManager}
            onChange={(dateRange) =>
              (pending.value = { ...pending.value, dateRange })
            }
          />
        </div>
        <div class="flex gap-2 pt-1">
          <button class="btn btn-primary btn-sm flex-1" onClick={applySheet}>
            <span class="material-symbols-outlined text-base">check</span>
            Apply
          </button>
        </div>
      </div>
    );

    return () => (
      <>
        <div class="hidden md:flex items-end gap-2 flex-wrap">
          {renderDesktop()}
        </div>
        <div class="md:hidden">{renderMobileSheet()}</div>
      </>
    );
  },
});
